import React, { useEffect, useMemo, useState } from "react";
import SchedulerGrid from "./SchedulerGrid";
import { VIEW_TYPES } from "./constants";
import SchedulerToolbar from "./SchedulerToolbar";
import { Event } from "./types/events";
import { Resource } from "./types/resources";
import { ViewType, TimeRange } from "./types/common";
import "./styles.css";
import {
  ResourceGroup,
  SchedulerTheme,
  SchedulerViewDefinition,
  TimelineScrollTarget,
  ViewInteractions,
  ViewConfig,
} from "./types/views";
import { defaultSchedulerTheme } from "./defaultSchedulerTheme";
import { getSchedulerViews } from "./viewRegistry";
import {
  getOptimisticDroppedEvent,
  mergeOptimisticEvents,
  reconcileOptimisticEvents,
} from "./optimisticEvents";

const ensureDate = (dateOrTimestamp: Date | number): Date => {
  if (dateOrTimestamp instanceof Date) return dateOrTimestamp;
  return new Date(dateOrTimestamp);
};

interface SchedulerProps {
  events: Event[];
  resources: Resource[];
  getEventResourceIds?: (event: Event) => (string | number)[];
  onEventChange?: (event: Event) => void;
  onEventDrop?: (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => void | Promise<any>;
  onViewChange?: (view: ViewType) => void;
  onEventClick?: ViewInteractions["onEventClick"];
  onTimeSlotClick?: ViewInteractions["onTimeSlotClick"];
  onResourceClick?: (resource: Resource) => void;
  customViews?: SchedulerViewDefinition[];
  visibleViewTypes?: ViewType[];
  initialView?: ViewType;
  activeView?: ViewType;
  startHour?: number;
  endHour?: number;
  className?: string;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  selectedDateRange?: TimeRange;
  onDateRangeChange?: (range: TimeRange) => void;
  useDateRange?: boolean;
  viewConfig?: ViewConfig;
  zoom?: number;
  showTooltip?: boolean;
  isDraggable?: boolean;
  showMapButton?: boolean;
  onMapToggle?: () => void;
  isMapVisible?: boolean;
  showSchedulerToggle?: boolean;
  onSchedulerToggle?: () => void;
  isSchedulerVisible?: boolean;
  schedulerTheme?: SchedulerTheme;
  getResourceGroups?: (
    resources: Resource[],
    viewConfig?: ViewConfig
  ) => ResourceGroup[];
  scrollTarget?: TimelineScrollTarget | null;
  highlightedEventId?: string | null;
}

export const Scheduler: React.FC<SchedulerProps> = ({
  events,
  resources,
  getEventResourceIds = (event) => event.assignedResources || [],
  onEventChange,
  onEventDrop,
  onViewChange,
  onEventClick,
  onTimeSlotClick,
  onResourceClick,
  customViews,
  visibleViewTypes,
  initialView = VIEW_TYPES.WEEK as ViewType,
  activeView,
  startHour = 0,
  endHour = 24,
  className,
  selectedDate,
  setSelectedDate,
  selectedDateRange,
  onDateRangeChange,
  useDateRange = false,
  viewConfig,
  zoom = 1,
  showTooltip = false,
  isDraggable = true,
  showMapButton = false,
  onMapToggle,
  isMapVisible = false,
  showSchedulerToggle = false,
  onSchedulerToggle,
  isSchedulerVisible = true,
  schedulerTheme,
  getResourceGroups,
  scrollTarget,
  highlightedEventId,
}) => {
  const [internalView, setInternalView] = useState<ViewType>(initialView);
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [optimisticEvents, setOptimisticEvents] = useState<Map<string, Event>>(
    () => new Map()
  );
  const currentView = activeView || internalView;

  const availableViews = useMemo(
    () => getSchedulerViews(customViews, visibleViewTypes),
    [customViews, visibleViewTypes]
  );

  useEffect(() => {
    if (!availableViews.length) {
      return;
    }

    const currentViewExists = availableViews.some((view) => view.type === currentView);
    if (currentViewExists) {
      return;
    }

    if (!activeView) {
      setInternalView(availableViews[0].type);
    }

    onViewChange?.(availableViews[0].type);
  }, [activeView, availableViews, currentView, onViewChange]);

  // Normalize events to ensure dates are Date objects
  const normalizedEvents = useMemo(
    () =>
      events.map((event) => ({
        ...event,
        timeRange: {
          ...event.timeRange,
          start: ensureDate(event.timeRange.start),
          end: ensureDate(event.timeRange.end),
        },
      })),
    [events]
  );

  useEffect(() => {
    setOptimisticEvents((currentOptimisticEvents) =>
      reconcileOptimisticEvents(
        normalizedEvents,
        currentOptimisticEvents,
        getEventResourceIds
      )
    );
  }, [getEventResourceIds, normalizedEvents]);

  const displayedEvents = useMemo(
    () => mergeOptimisticEvents(normalizedEvents, optimisticEvents),
    [normalizedEvents, optimisticEvents]
  );

  const resolvedViewConfig = useMemo(
    () => ({
      type: currentView,
      timeRange: selectedDateRange || {
        start: selectedDate,
        end: selectedDate,
      },
      scale:
        currentView === VIEW_TYPES.DAY
          ? "hour"
          : currentView === VIEW_TYPES.WEEK
            ? "day"
            : currentView === VIEW_TYPES.MONTH
              ? "month"
              : "day",
      showWeekends: true,
      workHours: viewConfig?.workHours || {
        start: startHour,
        end: endHour,
      },
      ...viewConfig,
    }),
    [currentView, endHour, selectedDate, selectedDateRange, startHour, viewConfig]
  );

  const resolvedSchedulerTheme = schedulerTheme || defaultSchedulerTheme;
  const resourceGroups = getResourceGroups?.(resources, resolvedViewConfig);

  const handleViewChange = (newView: ViewType) => {
    if (!activeView) {
      setInternalView(newView);
    }

    onViewChange?.(newView);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  const handleDateRangeChange = (range: TimeRange) => {
    onDateRangeChange?.(range);
  };

  const handleZoomChange = (newZoom: number) => {
    setCurrentZoom(newZoom);
  };

  const handleEventDrop = (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => {
    const currentEvent = displayedEvents.find(
      (event) => String(event.id) === String(eventId)
    );

    if (!currentEvent) {
      onEventDrop?.(eventId, newStartTime, newResource);
      return;
    }

    const optimisticEvent = getOptimisticDroppedEvent(
      currentEvent,
      newStartTime,
      newResource
    );

    setOptimisticEvents((currentOptimisticEvents) => {
      const nextOptimisticEvents = new Map(currentOptimisticEvents);
      nextOptimisticEvents.set(String(eventId), optimisticEvent);
      return nextOptimisticEvents;
    });

    try {
      Promise.resolve(onEventDrop?.(eventId, newStartTime, newResource)).catch(
        () => {
          setOptimisticEvents((currentOptimisticEvents) => {
            const nextOptimisticEvents = new Map(currentOptimisticEvents);
            nextOptimisticEvents.delete(String(eventId));
            return nextOptimisticEvents;
          });
        }
      );
    } catch (error) {
      setOptimisticEvents((currentOptimisticEvents) => {
        const nextOptimisticEvents = new Map(currentOptimisticEvents);
        nextOptimisticEvents.delete(String(eventId));
        return nextOptimisticEvents;
      });
      throw error;
    }
  };

  return (
    <div className={`scheduler-container ${className || ""}`}>
      <SchedulerToolbar
        currentView={currentView}
        onViewChange={handleViewChange}
        availableViews={availableViews.map(({ type, label }) => ({ type, label }))}
        onZoomChange={handleZoomChange}
        zoom={currentZoom}
        currentDate={selectedDate}
        onDateChange={handleDateChange}
        selectedDateRange={selectedDateRange}
        onDateRangeChange={handleDateRangeChange}
        useDateRange={useDateRange}
        showMapButton={showMapButton}
        onMapToggle={onMapToggle}
        isMapVisible={isMapVisible}
        showSchedulerToggle={showSchedulerToggle}
        onSchedulerToggle={onSchedulerToggle}
        isSchedulerVisible={isSchedulerVisible}
      />
      <SchedulerGrid
        events={displayedEvents}
        resources={resources}
        getEventResourceIds={getEventResourceIds}
        currentView={currentView}
        currentDate={selectedDate}
        onEventChange={onEventChange}
        onEventDrop={handleEventDrop}
        onEventClick={onEventClick}
        onTimeSlotClick={onTimeSlotClick}
        onResourceClick={onResourceClick}
        customViews={customViews}
        visibleViewTypes={visibleViewTypes}
        startHour={startHour}
        endHour={endHour}
        zoom={currentZoom}
        selectedDateRange={selectedDateRange}
        viewConfig={resolvedViewConfig}
        showTooltip={showTooltip}
        isDraggable={isDraggable}
        useDateRange={useDateRange}
        schedulerTheme={resolvedSchedulerTheme}
        resourceGroups={resourceGroups}
        scrollTarget={scrollTarget}
        highlightedEventId={highlightedEventId}
      />
    </div>
  );
};
